import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Paper,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import { api, API_BASE } from "../api"; // unified API base
import usePageTitle from "../hooks/usePageTitle";
import { buildItemUrl, slugify } from "../utils/itemUrl";
import { formatPrice, getPriceDollars } from "../utils/price";
import PriceConfidenceRibbon from "./PriceConfidenceRibbon";
import ImageWithFallback from "./ImageWithFallback";
import { MODERATION_REJECTION_MESSAGE, moderateTextFields } from "../utils/textModeration";
import { PriceValue, usePriceVisibility } from "../contexts/PriceVisibilityContext";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const withEbaySoldCompleted = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("ebay.")) return url;
    parsed.searchParams.set("LH_Sold", "1");
    parsed.searchParams.set("LH_Complete", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

const fmtUSD = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return "--";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
};

const fmtDeltaUSD = (deltaDollars) => {
  if (deltaDollars == null || !Number.isFinite(Number(deltaDollars))) return "";
  const absText = fmtUSD(Math.abs(Number(deltaDollars)));
  return Number(deltaDollars) >= 0 ? `+${absText}` : `-${absText}`;
};

const fmtPct = (pct) => {
  if (pct == null || !Number.isFinite(Number(pct))) return "";
  const n = Number(pct);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

function formatDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const getPriceChangeFromHistory = (history) => {
  const values = (Array.isArray(history) ? history : [])
    .map((row) => Number(row?.price))
    .filter((v) => Number.isFinite(v));
  if (values.length < 2) return null;

  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  const deltaDollars = last - prev;
  const pct = prev !== 0 ? (deltaDollars / prev) * 100 : null;
  return { deltaDollars, pct };
};

const PLACEHOLDER_TOKENS = new Set(["n/a", "unknown", "none", "null", "undefined", "misc", "for"]);

const cleanField = (value) => {
  if (value == null) return "";
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (PLACEHOLDER_TOKENS.has(text.toLowerCase())) return "";
  return text;
};

const isCardLikeItem = (item) => {
  const category = cleanField(item?.category).toLowerCase();
  const brand = cleanField(item?.brand).toLowerCase();
  return (
    category.includes("card") ||
    category.includes("pokemon") ||
    category.includes("one piece") ||
    brand === "pokemon" ||
    brand === "bandai"
  );
};

const formatImageSourceLabel = (value) => {
  const normalized = cleanField(value).toLowerCase();
  if (!normalized) return "";
  if (normalized === "external") return "External";
  if (normalized === "user") return "User Upload";
  if (normalized === "licensed") return "Licensed";
  if (normalized === "stock") return "Stock";
  return normalized.replace(/[_-]+/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const dedupeParts = (parts) => {
  const seen = new Set();
  const out = [];
  for (const raw of parts) {
    const part = cleanField(raw);
    if (!part) continue;
    const words = part.toLowerCase().split(/\s+/).filter((w) => w && !PLACEHOLDER_TOKENS.has(w));
    if (!words.length) continue;
    const key = words.join(" ");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(part);
  }
  return out;
};

const buildEbayQueryText = (item) => {
  const name = cleanField(item?.name);
  if (name) return name;
  return dedupeParts([
    cleanField(item?.player),
    cleanField(item?.character_name),
    cleanField(item?.set_name),
    cleanField(item?.number),
  ])
    .join(" ")
    .trim();
};

const buildDefaultCompTitle = (item) => {
  return cleanField(item?.name) || "";
};

const getItemDetailPriceDate = (item) => {
  if (!item) return "";

  const direct =
    item.latest_price_day ||
    item.price_day ||
    item.price_date ||
    item.latestPriceDay ||
    "";

  if (typeof direct === "string") {
    return direct.trim();
  }

  if (direct && typeof direct === "object") {
    const nested =
      direct.latest_price_day ||
      direct.price_day ||
      direct.price_date ||
      "";
    return typeof nested === "string" ? nested.trim() : "";
  }

  return "";
};

const buildEbaySearchUrl = (queryText, { includeSoldCompleted = false, sortNewest = false } = {}) => {
  if (!queryText) return "";
  const query = encodeURIComponent(queryText);
  const url = new URL(`https://www.ebay.com/sch/i.html?_nkw=${query}`);

  if (includeSoldCompleted) {
    url.searchParams.set("LH_Sold", "1");
    url.searchParams.set("LH_Complete", "1");
  }
  if (sortNewest) {
    url.searchParams.set("_sop", "10");
  }

  return url.toString();
};

const withEpnTracking = (url, customId) => {
  if (!url) return "";

  const campid = String(process.env.REACT_APP_EPN_CAMPID || "").trim();
  const mkrid = String(process.env.REACT_APP_EPN_MKRID || "").trim();
  const toolid = String(process.env.REACT_APP_EPN_TOOLID || "").trim();
  const siteid = String(process.env.REACT_APP_EPN_SITEID || "").trim();

  try {
    const parsed = new URL(url);
    parsed.searchParams.set("mkcid", "1");
    parsed.searchParams.set("mkrid", mkrid);
    parsed.searchParams.set("siteid", siteid);
    parsed.searchParams.set("campid", campid);
    parsed.searchParams.set("customid", String(customId || ""));
    parsed.searchParams.set("toolid", toolid);
    parsed.searchParams.set("mkevt", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

// --- confidence normalization + meter ---
const normalizeConfidence = (value) => {
  const v = String(value ?? "").trim().toLowerCase();

  if (
    [
      "high",
      "h",
      "strong",
      "high_active_exact",
      "high_active",
      "high_confidence",
    ].includes(v)
  ) {
    return "high";
  }

  if (
    [
      "medium",
      "med",
      "mid",
      "m",
      "medium_active_exact",
      "medium_active",
      "manual_sold_comp",
      "verified_manual_comp",
      "carry_forward_verified",
      "stale_carry_forward",
    ].includes(v)
  ) {
    return "medium";
  }

  if (
    [
      "low",
      "l",
      "weak",
      "low_active_adjusted",
      "low_active",
      "carry_forward",
      "carry_forward_recent",
      "carry_forward_infra_safe",
      "estimate_low",
      "category_median_fallback",
      "category_only_median_fallback",
      "item_type_median_fallback",
      "multi_source_weighted",
    ].includes(v)
  ) {
    return "low";
  }

  return "";
};

const formatVariantWithSerial = (editionVariant, serialRun) => {
  const edition = cleanField(editionVariant);
  const rawSerial = cleanField(serialRun);

  const normalizedSerial = rawSerial
    .replace(/^\//, "")
    .replace(/^(\d+)\s*\/\s*\1$/, "$1")
    .trim();

  const editionWithoutTrailingSerial = edition
    .replace(/\s*\/\s*\d+\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim();

  if (editionWithoutTrailingSerial && normalizedSerial) {
    return `${editionWithoutTrailingSerial} /${normalizedSerial}`;
  }

  if (editionWithoutTrailingSerial) return editionWithoutTrailingSerial;
  if (normalizedSerial) return `/${normalizedSerial}`;
  return "";
};

const buildVariantLabel = (v) => {
  const parts = [];
  const variantText = formatVariantWithSerial(v?.edition_variant, v?.serial_run);
  const condition = String(v?.condition_grade ?? "").trim();

  if (variantText) parts.push(variantText);
  if (condition && condition !== "New") parts.push(condition);
  if (Number(v?.is_signed) === 1) parts.push("Signed");
  return parts.length ? parts.join(" | ") : "Base";
};

const getItemDetailChartBucketConfig = (chartRange, labels = []) => {
  const firstDate = labels.length ? dayjs(labels[0]) : null;
  const lastDate = labels.length ? dayjs(labels[labels.length - 1]) : null;
  const spanMonths =
    firstDate && lastDate && firstDate.isValid() && lastDate.isValid()
      ? Math.max(0, lastDate.diff(firstDate, "month", true))
      : 0;

  switch (chartRange) {
    case "1m":
      return { type: "day", size: 1 };
    case "3m":
      return { type: "day", size: 7 };
    case "6m":
      return { type: "day", size: 14 };
    case "1yr":
      return { type: "month", size: 1 };
    default:
      if (spanMonths <= 3) return { type: "day", size: 7 };
      if (spanMonths <= 8) return { type: "day", size: 14 };
      return { type: "month", size: 1 };
  }
};

const buildItemDetailChartSeries = (rows, chartRange) => {
  if (!Array.isArray(rows) || !rows.length) {
    return { labels: [], data: [] };
  }

  const labels = rows.map((row) => row.date);
  const values = rows.map((row) => Number(Number(row.price).toFixed(2)));

  if (labels.length <= 1) {
    return { labels: [...labels], data: [...values] };
  }

  const bucketConfig = getItemDetailChartBucketConfig(chartRange, labels);
  if (bucketConfig.type === "day" && bucketConfig.size <= 1) {
    return { labels: [...labels], data: [...values] };
  }

  const parsedDates = labels.map((label) => dayjs(label));
  const firstValidDate = parsedDates.find((date) => date.isValid());
  if (!firstValidDate) {
    return { labels: [...labels], data: [...values] };
  }

  const reducedLabels = [];
  const reducedData = [];
  let activeBucketKey = null;
  let bucketLabel = null;
  let bucketValue = null;

  const flushBucket = () => {
    if (bucketLabel == null) return;
    reducedLabels.push(bucketLabel);
    reducedData.push(bucketValue ?? 0);
  };

  labels.forEach((label, index) => {
    const value = Number(values[index] ?? 0) || 0;
    const date = parsedDates[index];
    const effectiveDate = date.isValid() ? date : firstValidDate;
    const bucketKey =
      bucketConfig.type === "month"
        ? effectiveDate.startOf("month").format("YYYY-MM")
        : String(
            Math.floor(
              Math.max(0, effectiveDate.diff(firstValidDate, "day")) / bucketConfig.size
            )
          );

    if (activeBucketKey !== null && bucketKey !== activeBucketKey) {
      flushBucket();
      bucketValue = null;
    }

    activeBucketKey = bucketKey;
    bucketLabel = label;
    bucketValue = value;
  });

  flushBucket();

  const lastLabel = labels[labels.length - 1];
  const lastValue = Number(values[values.length - 1] ?? 0) || 0;
  if (reducedLabels[reducedLabels.length - 1] !== lastLabel) {
    reducedLabels.push(lastLabel);
    reducedData.push(lastValue);
  } else {
    reducedData[reducedData.length - 1] = lastValue;
  }

  return { labels: reducedLabels, data: reducedData };
};

const formatItemDetailChartAxisLabel = (label, chartRange, isMobile) => {
  const date = dayjs(label);
  if (!date.isValid()) return label;
  if (chartRange === "1yr" || chartRange === "max") {
    return isMobile ? date.format("MMM 'YY") : date.format("MMM YYYY");
  }
  return isMobile ? date.format("M/D") : date.format("MMM D");
};

const getItemDetailChartMaxTicks = (chartRange, isMobile) => {
  if (isMobile) {
    if (chartRange === "1m") return 5;
    if (chartRange === "3m") return 6;
    if (chartRange === "6m") return 6;
    return 5;
  }
  if (chartRange === "1m") return 8;
  if (chartRange === "3m") return 8;
  if (chartRange === "6m") return 7;
  return 6;
};

function getRangePerformance(values) {
  const clean = (Array.isArray(values) ? values : []).map(Number).filter(Number.isFinite);
  if (clean.length < 2) {
    return { hasData: false, delta: null, pct: null };
  }

  const first = clean[0];
  const last = clean[clean.length - 1];
  const delta = last - first;
  const pct = first !== 0 ? (delta / first) * 100 : null;

  return { hasData: true, first, last, delta, pct };
}

const formatSignedDollarChange = (value) => {
  if (!Number.isFinite(Number(value))) return "";
  const n = Number(value);
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${Math.abs(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  })}`;
};

const formatSignedPercentChange = (value) => {
  if (!Number.isFinite(Number(value))) return "";
  const n = Number(value);
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${Math.abs(n).toFixed(1)}%`;
};

const formatRarity = (r) =>
  r?.replace(/_/g, " ")?.replace(/\b\w/g, (c) => c.toUpperCase());
const safeText = (value) => (typeof value === "string" ? value : String(value ?? ""));

const ItemDetail = () => {
  const { showPrices, pricesLoaded } = usePriceVisibility();
  const { id, slug } = useParams();
  const [item, setItem] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [chartRange, setChartRange] = useState("max");
  const [variants, setVariants] = useState([]);
  const [rarityFilter, setRarityFilter] = useState("all");
  const [submittedComps, setSubmittedComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compDialogOpen, setCompDialogOpen] = useState(false);
  const [compSubmitting, setCompSubmitting] = useState(false);
  const [compError, setCompError] = useState("");
  const [compSnackbar, setCompSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [currentUser, setCurrentUser] = useState(() => readStoredUser());
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [compForm, setCompForm] = useState({
    source_name: "",
    source_url: "",
    title: "",
    price: "",
    sold_date: "",
    condition_note: "",
    comp_type: "sold",
    notes: "",
  });

  const norm = (v) => cleanField(v);
  const displayVariant = cleanField(item?.edition_variant);
  const displayPrintRun = cleanField(item?.serial_run).replace(/^\//, "");
  const showCardMetadata = isCardLikeItem(item);
  const displayImageSource = formatImageSourceLabel(item?.image_source);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  usePageTitle(item?.name || "");

  // support both { id } and { user_id }
  const user = currentUser;
  const userId = user?.id ?? user?.user_id ?? null;
  const priceDateDisplay = getItemDetailPriceDate(item);
  const rarityOptions = [
    "all",
    ...Array.from(
      new Set(
        variants
          .map((v) => (v?.secret_rarity || v?.rarity || "").trim())
          .filter((r) => r && r.length > 0)
      )
    ).sort(),
  ];

  const filteredVariants = variants.filter((v) => {
    if (rarityFilter === "all") return true;
    return (v?.secret_rarity || v?.rarity || "").trim() === rarityFilter;
  });

  const fetchComps = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/items/${id}/comps`, {
        withCredentials: true,
      });
      setSubmittedComps(Array.isArray(res.data?.comps) ? res.data.comps : []);
    } catch (err) {
      console.error("Item comps load failed:", err);
      setSubmittedComps([]);
    }
  };

  useEffect(() => {
    const syncUser = () => setCurrentUser(readStoredUser());
    syncUser();
    window.addEventListener("storage", syncUser);
    return () => window.removeEventListener("storage", syncUser);
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setItem(null);
        setVariants([]);
        setPriceHistory([]);
        setSubmittedComps([]);

        const itemRes = await axios.get(`${API_BASE}/api/item/${id}`, {
          withCredentials: true,
        });
        const itemData = itemRes.data;
        let variantRows = Array.isArray(itemData?.variants) ? itemData.variants : [];
        console.log("ItemDetail embedded variants:", itemData?.variants || []);
        let variantSource = "embedded";
        try {
          const vRes = await axios.get(`${API_BASE}/api/item/${id}/variants`, {
            withCredentials: true,
          });
          console.log("ItemDetail dedicated variants response:", vRes?.data?.variants || []);
          if (Array.isArray(vRes?.data?.variants) && vRes.data.variants.length) {
            variantRows = vRes.data.variants;
            variantSource = "dedicated";
          }
        } catch {
          // fallback to variants embedded in item detail payload
        }
        console.log("ItemDetail variants source:", variantSource);

        const histRes = await axios.get(`${API_BASE}/api/price-history/${id}`, {
          withCredentials: true,
        });
        const hist = Array.isArray(histRes.data) ? histRes.data : [];
        let comps = [];
        try {
          const compsRes = await axios.get(`${API_BASE}/api/items/${id}/comps`, {
            withCredentials: true,
          });
          comps = Array.isArray(compsRes.data?.comps) ? compsRes.data.comps : [];
        } catch (compErr) {
          console.error("Item comps load failed:", compErr);
        }

        if (!mounted) return;

        console.log("ITEM DETAIL PRICE DATE:", getItemDetailPriceDate(itemData));
        setItem(itemData);
        setVariants(variantRows);
        console.log("ItemDetail variants API rows:", variantRows);
        console.log(
          "ItemDetail variants with unresolved price:",
          variantRows.filter((row) => getPriceDollars(row) == null)
        );
        setPriceHistory(
          hist
            .map((row) => ({
              date: (() => {
                const rawDate = row.price_day || row.price_date;
                return rawDate ? dayjs(rawDate).format("YYYY-MM-DD") : "";
              })(),
              price: Number.isFinite(Number(row.price)) ? Number(row.price) : null,
            }))
            .filter((x) => x.price != null)
        );
        setSubmittedComps(comps);
      } catch (err) {
        console.error("Item detail load failed:", err);
        if (mounted) {
          setItem(null);
          setVariants([]);
          setPriceHistory([]);
          setSubmittedComps([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    const routeId = Number(id);
    const itemId = Number(item?.item_id);

    if (!Number.isFinite(routeId) || !Number.isFinite(itemId)) return;
    if (routeId !== itemId) return;

    const itemName = item.name || `Item ${item.item_id}`;
    const correctSlug = slugify(itemName);
    if (!correctSlug || slug === correctSlug) return;
    navigate(buildItemUrl({ ...item, name: itemName }), { replace: true });
  }, [id, item, slug, navigate]);

  useEffect(() => {
    const itemId = Number(item?.item_id);
    if (!Number.isFinite(itemId) || itemId <= 0 || !userId) {
      setWishlisted(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setWishlistLoading(true);
        const res = await api.get(`/api/wishlist/check/${itemId}`);
        if (!cancelled) {
          setWishlisted(Boolean(res.data?.wishlisted));
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Wishlist check failed:", err);
          setWishlisted(false);
        }
      } finally {
        if (!cancelled) setWishlistLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [item?.item_id, userId]);

  const handleAddToCollection = async () => {
    if (!userId) {
      alert("Please log in first.");
      navigate("/login");
      return;
    }

    try {
      await axios.post(`${API_BASE}/api/collection/add`, {
        userId,
        itemId: item.item_id,
      }, {
        withCredentials: true,
      });
      alert("✅ Added to your collection");
    } catch (e) {
      console.error(e);
      alert("❌ Could not add");
    }
  };

  const handleWishlistToggle = async () => {
    if (!userId) {
      alert("Please log in first.");
      navigate("/login");
      return;
    }

    const itemId = Number(item?.item_id);
    if (!Number.isFinite(itemId) || itemId <= 0) return;

    try {
      setWishlistLoading(true);
      if (wishlisted) {
        await api.delete(`/api/wishlist/remove/${itemId}`);
        setWishlisted(false);
      } else {
        await api.post("/api/wishlist/add", { item_id: itemId });
        setWishlisted(true);
      }
    } catch (err) {
      console.error("Wishlist toggle failed:", err);
      alert("Could not update wishlist.");
    } finally {
      setWishlistLoading(false);
    }
  };

  const displayHistory = useMemo(
    () => buildItemDetailChartSeries(priceHistory, chartRange),
    [priceHistory, chartRange]
  );

  const chartData = {
    labels: displayHistory.labels,
    datasets: [
      {
        data: displayHistory.data,
        tension: 0.1,
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
        pointRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const label = items?.[0]?.label;
            return label ? formatItemDetailChartAxisLabel(label, chartRange, false) : "";
          },
          label: (context) => {
            const value = context.raw;
            return formatPrice({ price: Number(value).toFixed(2) }) || "TBD";
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: getItemDetailChartMaxTicks(chartRange, isMobile),
          callback: function(value) {
            const label = this.getLabelForValue(value);
            return formatItemDetailChartAxisLabel(label, chartRange, isMobile);
          },
        },
      },
      y: {
        ticks: {
          callback: (val) => formatPrice({ price: Number(val).toFixed(2) }) || "TBD",
        },
      },
    },
  };

  const selectedRangePerformance = getRangePerformance(displayHistory.data);
  const selectedRangeColor = !selectedRangePerformance.hasData
    ? "text.secondary"
    : selectedRangePerformance.delta > 0
    ? "success.main"
    : selectedRangePerformance.delta < 0
    ? "error.main"
    : "text.secondary";
  const selectedRangeText = selectedRangePerformance.hasData
    ? `Selected Range: ${formatSignedDollarChange(selectedRangePerformance.delta)}${
        selectedRangePerformance.pct != null
          ? ` (${formatSignedPercentChange(selectedRangePerformance.pct)})`
          : ""
      }`
    : "Selected Range: Not enough data";

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!item) {
    return (
      <Typography sx={{ textAlign: "center", p: 5 }}>
        Item not found.
      </Typography>
    );
  }

  const apiDeltaDollars =
    item?.delta_cents != null && Number.isFinite(Number(item.delta_cents))
      ? Number(item.delta_cents) / 100
      : null;
  const apiPct =
    item?.change_pct != null && Number.isFinite(Number(item.change_pct))
      ? Number(item.change_pct)
      : null;
  const historyChange = getPriceChangeFromHistory(priceHistory);
  const changeDeltaDollars = apiDeltaDollars ?? historyChange?.deltaDollars ?? null;
  const changePct = apiPct ?? historyChange?.pct ?? null;
  const hasPriceChange = Number.isFinite(changeDeltaDollars) || Number.isFinite(changePct);
  const directionMetric = Number.isFinite(changePct) ? Number(changePct) : Number(changeDeltaDollars);
  const changeDirection = !hasPriceChange
    ? "new"
    : directionMetric > 0
    ? "up"
    : directionMetric < 0
    ? "down"
    : "flat";
  const changeArrow = changeDirection === "up" ? "\u2191" : changeDirection === "down" ? "\u2193" : "";
  const changeColor =
    changeDirection === "up"
      ? "success.main"
      : changeDirection === "down"
      ? "error.main"
      : "text.secondary";
  const changeText = hasPriceChange
    ? `${fmtDeltaUSD(changeDeltaDollars)}${fmtPct(changePct) ? ` (${fmtPct(changePct)})` : ""}`
    : "NEW";

  // Normalize confidence so it ALWAYS shows reliably
  const priceConfidence = normalizeConfidence(
    item.price_confidence ||
      item.pricing_confidence ||
      item.confidence ||
      item.priceConfidence ||
      item.pricingConfidence
  );

  const ebayQueryText = buildEbayQueryText(item);
  const compsUrl = buildEbaySearchUrl(ebayQueryText, { includeSoldCompleted: true });
  const shopUrl = buildEbaySearchUrl(ebayQueryText, { sortNewest: true });
  const trackingItemId = item?.item_id ?? id;
  const trackedShopHref = withEpnTracking(
    shopUrl,
    `itemdetail_shop_${item?.id ?? item?.item_id ?? id}`
  );
  const trackedCompsHref = withEpnTracking(
    withEbaySoldCompleted(compsUrl),
    `itemdetail_sold_${item?.id ?? item?.item_id ?? id}`
  );

  const trackOutbound = (destination, url) => {
    if (!url) return;
    void axios
      .post(`${API_BASE}/api/track/outbound`, {
        item_id: trackingItemId,
        destination,
        url,
        ts: new Date().toISOString(),
      })
      .catch(() => {});
  };

  const openOutbound = (destination, url) => {
    if (!url) return;
    trackOutbound(destination, url);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCompFieldChange = (event) => {
    const { name, value } = event.target;
    setCompForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetCompForm = () => {
    setCompForm({
      source_name: "",
      source_url: "",
      title: "",
      price: "",
      sold_date: "",
      condition_note: "",
      comp_type: "sold",
      notes: "",
    });
    setCompError("");
  };

  const handleCompDialogClose = () => {
    if (compSubmitting) return;
    setCompDialogOpen(false);
    setCompError("");
  };

  const openCompDialog = () => {
    const defaultTitle = buildDefaultCompTitle(item);

    setCompForm((prev) => ({
      ...prev,
      title: defaultTitle,
    }));

    setCompError("");
    setCompDialogOpen(true);
  };

  const handleCompSubmit = async () => {
    setCompError("");
    setCompSubmitting(true);

    try {
      const fallbackTitle = buildDefaultCompTitle(item);
      const finalTitle =
        String(compForm.title || "").trim() || fallbackTitle;
      // Frontend moderation: reject inappropriate free-text comp submissions.
      const moderationResult = moderateTextFields({
        source_name: compForm.source_name,
        title: finalTitle,
        condition_note: compForm.condition_note,
        notes: compForm.notes,
      });
      if (!moderationResult.ok) {
        setCompError(MODERATION_REJECTION_MESSAGE);
        return;
      }
      await axios.post(
        `${API_BASE}/api/items/${item.item_id}/comps`,
        {
          source_name: compForm.source_name,
          source_url: compForm.source_url,
          title: finalTitle,
          price: compForm.price,
          sold_date: compForm.sold_date,
          condition_note: compForm.condition_note,
          comp_type: compForm.comp_type,
          notes: compForm.notes,
        },
        {
          withCredentials: true,
        }
      );

      await fetchComps();
      setCompDialogOpen(false);
      resetCompForm();
      setCompSnackbar({
        open: true,
        message: "Comp submitted.",
        severity: "success",
      });
    } catch (err) {
      const message = safeText(
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Could not submit comp."
      );
      setCompError(message);
    } finally {
      setCompSubmitting(false);
    }
  };

  const makeSearchLink = (value) =>
    `/search?query=${encodeURIComponent(value)}`;
  const showVariants = variants.length > 1;

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        minHeight: "100vh",
        pt: isMobile ? 4 : 2,
        pb: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: 1200, md: 1140, lg: 1100 },
          mx: "auto",
          p: { xs: 2, md: 3.5, lg: 3 },
        }}
      >
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 3.5,
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0px 12px 32px rgba(0,0,0,0.06)",
            p: { xs: 2, md: 3, lg: 2.75 },
          }}
        >
          <Grid container spacing={{ xs: 4, md: 3 }} alignItems="stretch">
            {/* Image */}
            <Grid item xs={12} md={5} sx={{ display: { md: "flex" } }}>
              <Card
                sx={{
                  height: "100%",
                  width: "100%",
                  backgroundColor: "#F6F8FA",
                  borderRadius: 2.5,
                  border: "1px solid rgba(0,0,0,0.05)",
                  p: 0,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    p: { xs: 2, sm: 2.5, md: 2.5 },
                    minHeight: { xs: "unset", md: 420 },
                  }}
                >
                  <Box
                    component={ImageWithFallback}
                    src={item.image_url}
                    fallbackSrcs={[item.ref_image_url]}
                    placeholderSrc="/logos/default-item.jpg"
                    alt={item.name}
                    sx={{
                      width: "100%",
                      height: { xs: "auto", md: "100%" },
                      maxHeight: { xs: "70vh", sm: "75vh", md: 520 },
                      objectFit: { xs: "contain", md: "contain" },
                      objectPosition: "center",
                      display: "block",
                      bgcolor: "#f8f8f8",
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </Card>
            </Grid>

            {/* Details + Actions */}
            <Grid item xs={12} md={7} sx={{ display: { md: "flex" } }}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  width: "100%",
                  p: { xs: 3, md: 2.5 },
                  borderRadius: 2.5,
                  backgroundColor: "#F6F8FA",
                  border: "1px solid rgba(0,0,0,0.05)",
                  boxShadow: "none",
                }}
              >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
              {item.name}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "flex-start" : "center",
                justifyContent: "space-between",
                gap: isMobile ? 1.5 : 1.75,
                mb: 2,
              }}
            >
              {/* LEFT: price + confidence */}
              {showPrices && pricesLoaded && <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="h5" sx={{ color: "primary.light" }}>
                    <PriceValue>{formatPrice(item) || "TBD"}</PriceValue>
                  </Typography>
                  {showPrices && pricesLoaded && <Typography
                    variant="subtitle1"
                    sx={{
                      color: changeColor,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {changeArrow ? `${changeArrow} ` : ""}
                    {changeText}
                  </Typography>}
                </Box>

                {(priceConfidence || priceDateDisplay) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    {priceConfidence && <PriceConfidenceRibbon confidence={priceConfidence} />}
                    {priceDateDisplay && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Price last updated: {priceDateDisplay || "—"}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>}

              {/* RIGHT: buttons row */}
              <Box
                sx={{
                  width: "100%",
                  mt: { xs: 2, md: 0.5 },
                }}
              >
                <Grid container spacing={{ xs: 1.5, md: 1.25 }} alignItems="stretch">
                  <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      fullWidth
                      onClick={handleAddToCollection}
                      sx={{
                        minHeight: 52,
                        height: "100%",
                        whiteSpace: "nowrap",
                        py: 1.25,
                        fontWeight: 700,
                      }}
                      >
                      Add to Collection
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                    <Button
                      variant={wishlisted ? "outlined" : "contained"}
                      size="medium"
                      fullWidth
                      onClick={handleWishlistToggle}
                      disabled={wishlistLoading}
                      sx={{
                        minHeight: 52,
                        height: "100%",
                        whiteSpace: "nowrap",
                        py: 1.25,
                        fontWeight: 700,
                        bgcolor: wishlisted ? "transparent" : "#7B3FB3",
                        color: wishlisted ? "#7B3FB3" : "#fff",
                        borderColor: "#7B3FB3",
                        "&:hover": {
                          bgcolor: wishlisted ? "rgba(123, 63, 179, 0.08)" : "#6D35A3",
                          borderColor: "#6D35A3",
                        },
                      }}
                    >
                      {wishlistLoading ? "Checking..." : wishlisted ? "Wishlisted" : "Add to Wishlist"}
                    </Button>
                  </Grid>
                  {showPrices && pricesLoaded && <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      fullWidth
                      onClick={openCompDialog}
                      sx={{ minHeight: 44, height: "100%", whiteSpace: "nowrap" }}
                    >
                      Submit a Comp
                    </Button>
                  </Grid>}
                  <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                    <Button
                      component={RouterLink}
                      to={`/edit?itemId=${item.item_id}`}
                      variant="contained"
                      color="secondary"
                      size="small"
                      fullWidth
                      sx={{
                        minHeight: 44,
                        height: "100%",
                        whiteSpace: "nowrap",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      Submit an Edit
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      fullWidth
                      disabled={!trackedShopHref}
                      onClick={() => openOutbound("ebay_shop", trackedShopHref)}
                      sx={{ minHeight: 44, height: "100%", whiteSpace: "nowrap" }}
                    >
                      Shop on eBay
                    </Button>
                  </Grid>
                  {showPrices && pricesLoaded && <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      fullWidth
                      disabled={!trackedCompsHref}
                      onClick={() => openOutbound("ebay_comps", trackedCompsHref)}
                      sx={{ minHeight: 44, height: "100%", whiteSpace: "nowrap" }}
                    >
                      View Sold
                    </Button>
                  </Grid>}
                </Grid>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {norm(item.brand) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Brand:</strong>{" "}
                    <Link component={RouterLink} to={makeSearchLink(item.brand)}>
                      {norm(item.brand)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.year_released) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Year Released:</strong> {norm(item.year_released)}
                  </Typography>
                </Grid>
              )}
              {norm(item.category) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Category:</strong>{" "}
                    <Link
                      component={RouterLink}
                      to={makeSearchLink(item.category)}
                    >
                      {norm(item.category)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.team) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Team:</strong>{" "}
                    <Link component={RouterLink} to={makeSearchLink(item.team)}>
                      {norm(item.team)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.person_name) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Person:</strong>{" "}
                    <Link component={RouterLink} to={makeSearchLink(item.person_name)}>
                      {norm(item.person_name)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {!showCardMetadata && norm(item.item_type) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Item Type:</strong> {norm(item.item_type)}
                  </Typography>
                </Grid>
              )}
              {norm(item.player) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Player:</strong>{" "}
                    <Link
                      component={RouterLink}
                      to={makeSearchLink(item.player)}
                    >
                      {norm(item.player)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.character_name) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Character:</strong>{" "}
                    <Link
                      component={RouterLink}
                      to={makeSearchLink(item.character_name)}
                    >
                      {norm(item.character_name)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.set_name) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Set Name:</strong>{" "}
                    <Link
                      component={RouterLink}
                      to={makeSearchLink(item.set_name)}
                    >
                      {norm(item.set_name)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.series_collection) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Series:</strong>{" "}
                    <Link
                      component={RouterLink}
                      to={makeSearchLink(item.series_collection)}
                    >
                      {norm(item.series_collection)}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.number) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Number:</strong> {norm(item.number)}
                  </Typography>
                </Grid>
              )}
              {norm(item.publisher) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Publisher:</strong> {norm(item.publisher)}
                  </Typography>
                </Grid>
              )}
              {norm(item.issue_number) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Issue Number:</strong> {norm(item.issue_number)}
                  </Typography>
                </Grid>
              )}
              {norm(item.volume) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Volume:</strong> {norm(item.volume)}
                  </Typography>
                </Grid>
              )}
              {displayVariant && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Variant:</strong>{" "}
                    <Link
                      component={RouterLink}
                      to={makeSearchLink(displayVariant)}
                    >
                      {displayVariant}
                    </Link>
                  </Typography>
                </Grid>
              )}
              {norm(item.cover_variant) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Cover Variant:</strong> {norm(item.cover_variant)}
                  </Typography>
                </Grid>
              )}
              {norm(item.print_edition) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Print Edition:</strong> {norm(item.print_edition)}
                  </Typography>
                </Grid>
              )}
              {displayPrintRun && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Print Run:</strong> /{displayPrintRun}
                  </Typography>
                </Grid>
              )}
              {norm(item.theme) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Theme:</strong> {norm(item.theme)}
                  </Typography>
                </Grid>
              )}
              {norm(item.piece_count) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Piece Count:</strong> {norm(item.piece_count)}
                  </Typography>
                </Grid>
              )}
              {norm(item.minifigure_count) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Minifigures:</strong> {norm(item.minifigure_count)}
                  </Typography>
                </Grid>
              )}
              {norm(item.platform) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Platform:</strong> {norm(item.platform)}
                  </Typography>
                </Grid>
              )}
              {norm(item.region) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Region:</strong> {norm(item.region)}
                  </Typography>
                </Grid>
              )}
              {norm(item.authentication) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Authentication:</strong> {norm(item.authentication)}
                  </Typography>
                </Grid>
              )}
              {norm(item.inscription) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Inscription:</strong> {norm(item.inscription)}
                  </Typography>
                </Grid>
              )}
              {norm(item.exclusive_name) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Exclusive:</strong> {norm(item.exclusive_name)}
                  </Typography>
                </Grid>
              )}
              {norm(item.sticker_type) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Sticker:</strong> {norm(item.sticker_type)}
                  </Typography>
                </Grid>
              )}
              {norm(item.size_variant) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Size:</strong> {norm(item.size_variant)}
                  </Typography>
                </Grid>
              )}
              {showCardMetadata && norm(item.item_type) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Item Type:</strong> {norm(item.item_type)}
                  </Typography>
                </Grid>
              )}
              {showCardMetadata && norm(item.artist) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Illustrator / Artist:</strong> {norm(item.artist)}
                  </Typography>
                </Grid>
              )}
              {showCardMetadata && norm(item.language) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Language:</strong> {norm(item.language)}
                  </Typography>
                </Grid>
              )}
              {showCardMetadata && norm(item.secret_rarity) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Secret Rarity:</strong> {norm(item.secret_rarity)}
                  </Typography>
                </Grid>
              )}
              {showCardMetadata && displayImageSource && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Image Source:</strong> {displayImageSource}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography>
                  <strong>Signed:</strong> {item.is_signed ? "Yes" : "No"}
                </Typography>
              </Grid>
              {norm(item.item_condition) && (
                <Grid item xs={6}>
                  <Typography>
                    <strong>Condition:</strong> {norm(item.item_condition)}
                  </Typography>
                </Grid>
              )}
            </Grid>

              </Paper>
            </Grid>
          </Grid>
        </Box>

        {showVariants && (
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 3.5,
              backgroundColor: "background.paper",
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.05)",
            }}
          >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Other Versions
              </Typography>
              <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  select
                  label="Rarity"
                  value={rarityFilter}
                  onChange={(e) => setRarityFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="all">All Rarities</MenuItem>
                  {rarityOptions
                    .filter((r) => r !== "all")
                    .map((r) => (
                      <MenuItem key={r} value={r}>
                        {formatRarity(r)}
                      </MenuItem>
                    ))}
                </TextField>
              </Box>
              <Grid container spacing={2}>
                {filteredVariants.map((v) => {
                  const isCurrent = Number(v.item_id) === Number(item.item_id);
                  console.log("ItemDetail variant price debug:", {
                    item_id: v?.item_id,
                    name: v?.name,
                    price: v?.price,
                    price_cents: v?.price_cents,
                    cents: v?.cents,
                    latest_price_cents: v?.latest_price_cents,
                    latestPriceCents: v?.latestPriceCents,
                    raw: v,
                  });
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`variant-${v.item_id}`}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 2.5,
                        border: isCurrent
                          ? `2px solid ${theme.palette.primary.main}`
                          : "1px solid rgba(0,0,0,0.08)",
                        backgroundColor: "#F6F8FA",
                        overflow: "hidden",
                        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: theme.shadows[6],
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => navigate(buildItemUrl(v))}
                        disabled={isCurrent}
                        sx={{ height: "100%", opacity: isCurrent ? 0.95 : 1 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            minHeight: 360,
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              height: 220,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 2,
                              background: "linear-gradient(180deg, #FFFFFF 0%, #EEF3F7 100%)",
                              borderBottom: "1px solid rgba(0,0,0,0.05)",
                            }}
                          >
                            <Box
                              component={ImageWithFallback}
                              src={v.image_url}
                              fallbackSrcs={[v.ref_image_url]}
                              placeholderSrc="/logos/default-item.jpg"
                              alt={v.name || buildVariantLabel(v)}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </Box>

                          <CardContent
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              p: 2,
                              "&:last-child": { pb: 2 },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                  color: "text.primary",
                                  pr: 0.5,
                                }}
                              >
                                {buildVariantLabel(v)}
                              </Typography>

                              {isCurrent && <Chip size="small" label="Current" color="primary" />}
                            </Box>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                lineHeight: 1.45,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                minHeight: "4.2em",
                                mb: 1.5,
                              }}
                            >
                              {v.name || "Untitled variant"}
                            </Typography>

                            {showPrices && pricesLoaded && <Box sx={{ mt: "auto" }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: "primary.light",
                                  letterSpacing: 0.1,
                                }}
                              >
                                <PriceValue>{formatPrice(v) || "TBD"}</PriceValue>
                              </Typography>
                            </Box>}
                          </CardContent>
                        </Box>
                      </CardActionArea>
                    </Card>
                    </Grid>
                  );
                })}
              </Grid>
          </Box>
        )}

        {/* Price History */}
        {priceHistory.length > 0 && showPrices && pricesLoaded && (
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 3.5,
              backgroundColor: "background.paper",
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.05)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1.5 }}>
              Price History
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
                alignItems: "center",
              }}
            >
              {[
                { value: "1m", label: "1M" },
                { value: "3m", label: "3M" },
                { value: "6m", label: "6M" },
                { value: "1yr", label: "1YR" },
                { value: "max", label: "MAX" },
              ].map((range) => (
                <Button
                  key={range.value}
                  variant={chartRange === range.value ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setChartRange(range.value)}
                  sx={{ minWidth: 64 }}
                >
                  {range.label}
                </Button>
              ))}
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: selectedRangeColor,
                mb: 1.5,
              }}
            >
              {selectedRangeText}
            </Typography>

            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: isMobile ? 200 : 400,
              }}
            >
              <Line data={chartData} options={chartOptions} />
            </Box>
          </Box>
        )}

        {submittedComps.length > 0 && showPrices && pricesLoaded && (
          <Box
            sx={{
              mt: 3,
              p: { xs: 3, md: 2.5 },
              borderRadius: 3.5,
              backgroundColor: "background.paper",
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.05)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Recent Submitted Comps
            </Typography>
            <Grid container spacing={1.5}>
              {submittedComps.slice(0, 3).map((comp) => (
                <Grid item xs={12} md={4} key={comp.comp_id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      height: "100%",
                      borderRadius: 2.5,
                      backgroundColor: "#F6F8FA",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {comp.source_name}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, color: "primary.light" }}>
                      {fmtUSD(Number(comp.price))}
                    </Typography>
                    {comp.sold_date && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Sold: {formatDate(comp.sold_date)}
                      </Typography>
                    )}
                    {comp.source_url && (
                      <Link
                        href={comp.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ display: "inline-block", mt: 1, fontSize: "0.85rem" }}
                      >
                        View listing
                      </Link>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      <Dialog
        open={compDialogOpen}
        onClose={handleCompDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Submit a Comp</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Source"
                name="source_name"
                value={compForm.source_name}
                onChange={handleCompFieldChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Comp Type"
                name="comp_type"
                value={compForm.comp_type}
                onChange={handleCompFieldChange}
                fullWidth
                select
              >
                <MenuItem value="sold">Sold</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="auction">Auction</MenuItem>
                <MenuItem value="best_offer_estimate">Best Offer Estimate</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Listing URL"
                name="source_url"
                value={compForm.source_url}
                onChange={handleCompFieldChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Title"
                name="title"
                value={compForm.title}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  "& .MuiInputBase-input": { color: "#666" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sold Price"
                name="price"
                type="number"
                value={compForm.price}
                onChange={handleCompFieldChange}
                fullWidth
                required
                inputProps={{ min: 0.01, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sold Date"
                name="sold_date"
                type="date"
                value={compForm.sold_date}
                onChange={handleCompFieldChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Condition"
                name="condition_note"
                value={compForm.condition_note}
                onChange={handleCompFieldChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                name="notes"
                value={compForm.notes}
                onChange={handleCompFieldChange}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
            {compError && (
              <Grid item xs={12}>
                <Alert severity="error">{safeText(compError)}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCompDialogClose} disabled={compSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCompSubmit}
            disabled={compSubmitting}
          >
            {compSubmitting ? "Submitting..." : "Submit Comp"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={compSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setCompSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={compSnackbar.severity}
          onClose={() => setCompSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {safeText(compSnackbar.message)}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ItemDetail;
